import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import ContactCTA from "../components/ContactCTA";
import VideoEmbed from "../components/VideoEmbed";
import { COMPANY } from "../data/site";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/** Convert content with line breaks into safe paragraphs. */
function renderContent(text) {
  if (!text) return null;
  const blocks = text.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, i) => (
    <p
      key={i}
      className="text-[#18221A] leading-relaxed text-lg mb-5 whitespace-pre-line"
    >
      {block}
    </p>
  ));
}

function videoEmbed(url) {
  if (!url) return null;
  return <VideoEmbed url={url} />;
}

function getEdgeBlogPost(slug) {
  if (typeof window === "undefined") return null;
  const value = window.__TF_BLOG_POST__;
  return value && value.slug === slug ? value : null;
}

export default function BloggPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const initialPost = getEdgeBlogPost(slug);
  const [post, setPost] = useState(initialPost);
  const [loading, setLoading] = useState(!initialPost);

  useEffect(() => {
    const edgePost = getEdgeBlogPost(slug);
    if (edgePost) {
      window.__TF_BLOG_POST__ = null;
      setPost(edgePost);
      setLoading(false);
      return;
    }

    setLoading(true);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="section-pad text-[#5A6258] text-center">Laddar…</div>
    );
  }

  if (!post) {
    return (
      <div className="section-pad text-center">
        <SEO
          title="Inlägget hittades inte | Trädfällarna Laholm"
          description="Det här blogginlägget finns inte."
          noindex
          path={`/blogg/${slug}`}
        />
        <h1 className="font-heading text-3xl font-bold text-[#1B2A1E] mb-3">
          Inlägget kunde inte hittas
        </h1>
        <p className="text-[#3a4338] mb-6">
          Det här inlägget finns inte längre. Det kanske har avpublicerats.
        </p>
        <button
          onClick={() => navigate("/blogg")}
          className="btn-primary"
          data-testid="blog-post-back-empty"
        >
          <ArrowLeft className="w-4 h-4" /> Till bloggen
        </button>
      </div>
    );
  }

  const title = post.meta_title || post.title;
  const description =
    post.meta_description || post.excerpt || `${post.title} – ${COMPANY.name}`;
  const ogImage = post.image
    ? post.image.startsWith("http")
      ? post.image
      : `${COMPANY.primaryDomain}${post.image}`
    : undefined;

  return (
    <article data-testid="blog-post" className="bg-[#F7F5F0]">
      <SEO
        title={`${title} | Blogg | Trädfällarna`}
        description={description}
        path={`/blogg/${post.slug}`}
        type="article"
        image={ogImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description,
            image: ogImage,
            datePublished: post.published_at || post.created_at,
            dateModified: post.published_at || post.created_at,
            author: { "@type": "Organization", name: COMPANY.name },
            publisher: { "@type": "Organization", name: COMPANY.name },
            mainEntityOfPage: `${COMPANY.primaryDomain}/blogg/${post.slug}`,
          })}
        </script>
      </SEO>

      <header className="bg-[#1B2A1E] text-white tf-grain relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
          <Link
            to="/blogg"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-[#F2A57C] hover:text-white font-semibold text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Tillbaka till bloggen
          </Link>
          <div className="flex items-center gap-2 text-xs text-[#A8B0A4] mb-4 uppercase tracking-[0.16em] font-semibold">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(post.published_at || post.created_at)}
          </div>
          <h1
            data-testid="blog-post-title"
            className="font-heading text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight"
          >
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-5 text-[#D9DCD3] text-lg leading-relaxed">
              {post.excerpt}
            </p>
          )}
        </div>
      </header>

      {post.image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="rounded-xl overflow-hidden border border-[#E2E0D8] bg-white shadow-xl">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-auto object-cover"
              onError={(e) => {
                // Hide broken images; wrapper stays for layout stability.
                e.currentTarget.style.display = "none";
                const wrap = e.currentTarget.parentElement;
                if (wrap) wrap.style.minHeight = "0";
              }}
            />
          </div>
        </div>
      )}

      <section className="section-pad">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContent(post.content)}
          {post.video_url && <div className="my-8">{videoEmbed(post.video_url)}</div>}
          {Array.isArray(post.images) && post.images.length > 0 && (
            <div
              data-testid="blog-post-gallery"
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
            >
              {post.images.map((src, i) => (
                <a
                  key={`${src}-${i}`}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`blog-post-gallery-item-${i}`}
                  className="block rounded-xl overflow-hidden border border-[#E2E0D8] bg-white aspect-[4/3] hover:border-[#D95D2E] transition-colors"
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <ContactCTA />
    </article>
  );
}
